import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TOOL_MATERIALS, TOOL_TYPES } from '@bridgewerk/shared';
import type { ToolLibraryRecord } from '@bridgewerk/shared';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useCreateToolLibraryEntry } from '../hooks/useToolLibrary.js';

const TOOL_TYPE_VALUES = TOOL_TYPES as readonly string[];
const TOOL_MATERIAL_VALUES = TOOL_MATERIALS as readonly string[];

const schema = z.object({
  description:           z.string().min(1, 'Description is required').max(120),
  type:                  z.enum(TOOL_TYPES),
  material:              z.enum(TOOL_MATERIALS),
  manufacturer:          z.string().max(80).optional().or(z.literal('')),
  part_number:           z.string().max(80).optional().or(z.literal('')),
  diameter_mm:           z.coerce.number().positive().optional().or(z.literal('')),
  expected_life_cuts:    z.coerce.number().int().positive().optional().or(z.literal('')),
  expected_life_minutes: z.coerce.number().positive().optional().or(z.literal('')),
  notes:                 z.string().max(500).optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

export interface CreateToolLibraryFormProps {
  readonly shopId: string;
  readonly onCreated?: (record: ToolLibraryRecord) => void;
  readonly onCancel?: () => void;
}

const FIELD_CLASS =
  'w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-text placeholder:text-text-dim focus:border-accent focus:outline-none';
const LABEL_CLASS = 'block text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim mb-1';

export function CreateToolLibraryForm({
  shopId,
  onCreated,
  onCancel,
}: CreateToolLibraryFormProps): JSX.Element {
  const toast = useToast();
  const create = useCreateToolLibraryEntry(shopId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: '',
      type: 'endmill',
      material: 'carbide',
      manufacturer: '',
      part_number: '',
      diameter_mm: '' as unknown as number,
      expected_life_cuts: '' as unknown as number,
      expected_life_minutes: '' as unknown as number,
      notes: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const record = await create.mutateAsync({
        description: values.description,
        type:        values.type,
        material:    values.material,
        manufacturer:          emptyToNull(values.manufacturer),
        part_number:           emptyToNull(values.part_number),
        diameter_mm:           numericOrNull(values.diameter_mm),
        expected_life_cuts:    numericOrNull(values.expected_life_cuts),
        expected_life_minutes: numericOrNull(values.expected_life_minutes),
        notes:                 emptyToNull(values.notes),
      });
      toast.push({ tone: 'success', title: 'Tool added', description: record.description });
      reset();
      onCreated?.(record);
    } catch (err) {
      toast.push({
        tone: 'critical',
        title: 'Could not add tool',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Description *" error={errors.description?.message}>
          <input
            type="text"
            className={FIELD_CLASS}
            placeholder="10mm 4-flute carbide endmill"
            {...register('description')}
          />
        </Field>

        <Field label="Type *" error={errors.type?.message}>
          <select className={FIELD_CLASS} {...register('type')}>
            {TOOL_TYPE_VALUES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>

        <Field label="Material *" error={errors.material?.message}>
          <select className={FIELD_CLASS} {...register('material')}>
            {TOOL_MATERIAL_VALUES.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>

        <Field label="Diameter (mm)" error={errors.diameter_mm?.message}>
          <input
            type="number"
            step="0.001"
            className={FIELD_CLASS}
            placeholder="10"
            {...register('diameter_mm')}
          />
        </Field>

        <Field label="Manufacturer" error={errors.manufacturer?.message}>
          <input type="text" className={FIELD_CLASS} placeholder="Kennametal" {...register('manufacturer')} />
        </Field>

        <Field label="Part number" error={errors.part_number?.message}>
          <input type="text" className={FIELD_CLASS} placeholder="KN-1234" {...register('part_number')} />
        </Field>

        <Field label="Expected life (cuts)" error={errors.expected_life_cuts?.message}>
          <input
            type="number"
            min="1"
            className={FIELD_CLASS}
            placeholder="1200"
            {...register('expected_life_cuts')}
          />
        </Field>

        <Field label="Expected life (minutes)" error={errors.expected_life_minutes?.message}>
          <input
            type="number"
            min="1"
            className={FIELD_CLASS}
            placeholder="120"
            {...register('expected_life_minutes')}
          />
        </Field>
      </div>

      <Field label="Notes" error={errors.notes?.message}>
        <textarea
          rows={2}
          className={FIELD_CLASS}
          placeholder="Optional. Roughing pass only."
          {...register('notes')}
        />
      </Field>

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Add tool'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  readonly label: string;
  readonly error?: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red font-mono">{error}</span>}
    </label>
  );
}

function emptyToNull(v: string | undefined): string | null {
  if (v === undefined || v === '') return null;
  return v;
}

function numericOrNull(v: number | string | undefined): number | null {
  if (v === undefined || v === '' || v === null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
